/**
 * Home prompts, pure rules (no React): what each prompt says and does, how
 * much it matters to each role, the per-user snooze, and which updates
 * deserve a pop-up. The counts come from `GET /v1/shell/prompts`, which only
 * counts what the user may open in the offices in view.
 *
 * Modelled on Atlas (zajo.click Dashboard), which the owner finds hard to
 * miss: full-width tinted cards at the very top, one "N people are waiting
 * on us" card with a row per kind (count, oldest wait, per-office split,
 * one click to the list), unread texts on their own card, and operational
 * problems (answer rate, PMS sync) on their own cards. Rose when urgent,
 * orange otherwise. Velora adds a small per-user snooze, role ordering and
 * a pop-up for new urgent items.
 *
 * Owner 2026-10-21 on the live cards: "way too big" (two rose cards, ~330px
 * before any content). Home now shows one slim strip of count chips
 * ("17 missed calls", "27 texts", ...), still rose and still first; the
 * cards are its expanded view, opened per user.
 */
import type { DashboardRole, HomeLayout, HomePrompt, HomePromptKey } from '@velora/contracts/shell';

export interface PromptMeta {
  /** "3 missed calls with no call back" */
  title: (n: number) => string;
  /** One-click action label. */
  action: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const PROMPT_META: Readonly<Record<HomePromptKey, PromptMeta>> = {
  missed_calls: {
    title: (n) => `${plural(n, 'missed call', 'missed calls')} with no call back`,
    action: 'Call back',
  },
  voicemails: {
    title: (n) => `${plural(n, 'voicemail', 'voicemails')} nobody has listened to`,
    action: 'Listen',
  },
  leads: {
    title: (n) => `${plural(n, 'new lead', 'new leads')} not contacted yet`,
    action: 'Contact',
  },
  texts: {
    title: (n) => `${plural(n, 'unanswered text', 'unanswered texts')}: no staff reply yet`,
    action: 'Reply',
  },
  missed_call_replies: {
    title: (n) =>
      `${plural(n, 'patient', 'patients')} replied to our missed-call text and nobody answered`,
    action: 'Reply',
  },
  portal_messages: {
    title: (n) => `${plural(n, 'portal message', 'portal messages')} waiting for a reply`,
    action: 'Reply',
  },
  ai_calls: {
    title: (n) =>
      `${plural(n, 'call', 'calls')} flagged for follow-up by the call review (staff-handled calls)`,
    action: 'Review',
  },
  after_hours_ai: {
    title: (n) =>
      `${plural(n, 'after-hours AI call', 'after-hours AI calls')} waiting for a call back`,
    action: 'Call back',
  },
  answer_rate: {
    title: (n) => `Answer rate is down today: ${plural(n, 'call', 'calls')} not answered`,
    action: 'See calls',
  },
  booking_requests: {
    title: (n) => `${plural(n, 'online booking request', 'online booking requests')} waiting`,
    action: 'Review',
  },
  portal_requests: {
    title: (n) => `${plural(n, 'portal request', 'portal requests')} waiting`,
    action: 'Open',
  },
};

/**
 * How much each prompt matters to each role (0 = not shown on that role's
 * Home). The front desk works the phones and messages first; clinicians
 * see their own list in Needs attention instead.
 */
export const PROMPT_WEIGHT: Readonly<Record<HomePromptKey, Record<DashboardRole, number>>> = {
  missed_calls: {
    front_desk: 100,
    owner: 85,
    treatment_coordinator: 80,
    billing: 40,
    dentist: 0,
    hygienist: 0,
  },
  voicemails: {
    front_desk: 98,
    owner: 80,
    treatment_coordinator: 78,
    billing: 40,
    dentist: 0,
    hygienist: 0,
  },
  texts: {
    front_desk: 95,
    treatment_coordinator: 75,
    owner: 70,
    billing: 45,
    dentist: 0,
    hygienist: 0,
  },
  missed_call_replies: {
    front_desk: 97,
    owner: 82,
    treatment_coordinator: 76,
    billing: 40,
    dentist: 0,
    hygienist: 0,
  },
  after_hours_ai: {
    front_desk: 96,
    owner: 82,
    treatment_coordinator: 70,
    billing: 0,
    dentist: 0,
    hygienist: 0,
  },
  leads: {
    treatment_coordinator: 95,
    front_desk: 92,
    owner: 85,
    billing: 0,
    dentist: 0,
    hygienist: 0,
  },
  booking_requests: {
    front_desk: 88,
    treatment_coordinator: 70,
    owner: 65,
    billing: 0,
    dentist: 0,
    hygienist: 0,
  },
  portal_messages: {
    front_desk: 85,
    treatment_coordinator: 65,
    owner: 60,
    billing: 45,
    dentist: 0,
    hygienist: 0,
  },
  portal_requests: {
    front_desk: 80,
    owner: 60,
    treatment_coordinator: 60,
    billing: 50,
    dentist: 0,
    hygienist: 0,
  },
  ai_calls: {
    owner: 80,
    front_desk: 75,
    treatment_coordinator: 60,
    billing: 0,
    dentist: 0,
    hygienist: 0,
  },
  answer_rate: {
    owner: 90,
    front_desk: 70,
    treatment_coordinator: 40,
    billing: 0,
    dentist: 0,
    hygienist: 0,
  },
};

export type Snoozes = NonNullable<HomeLayout['snoozed']>;

/** Anything a snooze can hold: a prompt, or a Home card such as the sync card. */
export interface Snoozable {
  key: string;
  count: number;
}

/** Snoozed and not back yet: before `until`, and nothing new since (count not higher). */
export function isSnoozed(p: Snoozable, snoozed: Snoozes | undefined, now: number): boolean {
  const s = snoozed?.[p.key];
  if (!s) return false;
  return Date.parse(s.until) > now && p.count <= s.n;
}

/** The prompts this role sees, urgent first, then by weight, then by count. */
export function rankPrompts(
  prompts: readonly HomePrompt[],
  role: DashboardRole,
  snoozed: Snoozes | undefined,
  now: number,
): { shown: HomePrompt[]; snoozed: HomePrompt[] } {
  const forRole = prompts.filter((p) => p.count > 0 && PROMPT_WEIGHT[p.key][role] > 0);
  const shown = forRole
    .filter((p) => !isSnoozed(p, snoozed, now))
    .sort(
      (a, b) =>
        (a.severity === 'urgent' ? 0 : 1) - (b.severity === 'urgent' ? 0 : 1) ||
        PROMPT_WEIGHT[b.key][role] - PROMPT_WEIGHT[a.key][role] ||
        b.count - a.count,
    );
  return { shown, snoozed: forRole.filter((p) => isSnoozed(p, snoozed, now)) };
}

export const SNOOZE_OPTIONS = [
  { id: '1h', label: 'For 1 hour' },
  { id: '4h', label: 'For 4 hours' },
  { id: 'tomorrow', label: 'Until tomorrow' },
] as const;
export type SnoozeOption = (typeof SNOOZE_OPTIONS)[number]['id'];

/** When a snooze ends; "tomorrow" is 7 am tomorrow in the browser's time. */
export function snoozeUntil(option: SnoozeOption, now: Date): Date {
  if (option === '1h') return new Date(now.getTime() + 3_600_000);
  if (option === '4h') return new Date(now.getTime() + 4 * 3_600_000);
  const t = new Date(now);
  t.setDate(t.getDate() + 1);
  t.setHours(7, 0, 0, 0);
  return t;
}

/** The layout with `p` (or every item of a card) snoozed, expired snoozes dropped. */
export function snooze(
  layout: HomeLayout,
  p: Snoozable | readonly Snoozable[],
  until: Date,
  now: number,
): HomeLayout {
  const kept: Snoozes = {};
  for (const [k, v] of Object.entries(layout.snoozed ?? {})) {
    if (Date.parse(v.until) > now) kept[k] = v;
  }
  for (const x of Array.isArray(p) ? p : [p as Snoozable]) {
    kept[x.key] = { until: until.toISOString(), n: x.count };
  }
  return { ...layout, snoozed: kept };
}

/** The layout with every snooze cleared. */
export function unsnoozeAll(layout: HomeLayout): HomeLayout {
  const { snoozed: _s, ...rest } = layout;
  return rest;
}

/**
 * Urgent prompts that are new since the last refresh (were not urgent, or
 * grew): what the pop-up announces. Nothing on the first load.
 */
export function newUrgent(
  prev: readonly HomePrompt[] | null,
  next: readonly HomePrompt[],
): HomePrompt[] {
  if (prev === null) return [];
  const before = new Map(prev.map((p) => [p.key, p]));
  return next.filter((p) => {
    if (p.severity !== 'urgent') return false;
    const b = before.get(p.key);
    return !b || b.severity !== 'urgent' || p.count > b.count;
  });
}

/* ---------- the cards ---------- */

/**
 * Prompts that are a person waiting: one "N people are waiting on us" card,
 * a row each. Patient texts get their own card (Atlas's "unseen SMS"); the
 * answer-rate dip is an operational card of its own.
 */
export const WAITING_KEYS: readonly HomePromptKey[] = [
  'missed_calls',
  'missed_call_replies',
  'voicemails',
  'after_hours_ai',
  'leads',
  'portal_messages',
  'ai_calls',
  'booking_requests',
  'portal_requests',
];

export interface PromptCards {
  /** Operational problems, each its own card (answer-rate dip). */
  ops: HomePrompt[];
  /** Rows of the "people waiting" card, in the role's order. */
  waiting: HomePrompt[];
  /** The unread-texts card. */
  texts: HomePrompt | null;
}

/** Splits the role's ranked prompts into Home's cards (pure, keeps the order). */
export function toCards(shown: readonly HomePrompt[]): PromptCards {
  return {
    ops: shown.filter((p) => p.key === 'answer_rate'),
    waiting: shown.filter((p) => WAITING_KEYS.includes(p.key)),
    texts: shown.find((p) => p.key === 'texts') ?? null,
  };
}

/** "19 people are waiting on us" (the rows' counts added up). */
export function waitingTitle(rows: readonly HomePrompt[]): string {
  const n = rows.reduce((t, r) => t + r.count, 0);
  return `${n} ${n === 1 ? 'person is' : 'people are'} waiting on us`;
}

/**
 * "McLaren 5 · Signature 4": the per-office split, only when more than one
 * office is in view (with one office it says nothing new).
 */
export function officeSplitLabel(
  byOffice: HomePrompt['byOffice'] | undefined,
  nameOf: (id: string) => string | undefined,
  officesInView: number,
): string | null {
  if (officesInView < 2 || !byOffice || byOffice.length === 0) return null;
  return byOffice.map((o) => `${nameOf(o.locationId) ?? 'Office'} ${o.count}`).join(' · ');
}

/**
 * The PMS sync card: linked offices whose core feed is late. A PulseSync
 * office carries the API's `syncLevel` (its connector interval: warning
 * past 2x the interval plus 2 minutes, error past 4x), the same rule as the
 * Sync status page and the top-bar chip, so "last sync 4m ago" on a
 * 5-minute connector is never an alert. Without it (older API, non-PulseSync
 * link): degraded, or no read for over 30 minutes. Pure.
 */
export interface SyncRow {
  locationId: string;
  pmsSystem: string | null;
  lastSyncedAt: string | null;
  degraded?: boolean | undefined;
  syncLevel?: 'ok' | 'warning' | 'error' | 'unknown' | undefined;
}
export interface SyncProblem {
  locationId: string;
  lastSyncedAt: string | null;
  /** error: not updating; warning: delayed. */
  level: 'warning' | 'error';
}
export function syncProblems(
  rows: readonly SyncRow[],
  now: number,
  offlineMs = 30 * 60_000,
): SyncProblem[] {
  const out: SyncProblem[] = [];
  for (const r of rows) {
    if (r.pmsSystem === null) continue;
    let level: SyncProblem['level'] | null = null;
    if (r.syncLevel !== undefined) {
      level = r.syncLevel === 'error' ? 'error' : r.syncLevel === 'warning' ? 'warning' : null;
    } else if (r.lastSyncedAt !== null) {
      // not synced yet (null) is setup, not an outage
      const t = Date.parse(r.lastSyncedAt);
      if (Number.isFinite(t) && now - t > offlineMs) level = 'error';
      else if (r.degraded === true) level = 'warning';
    } else if (r.degraded === true) {
      level = 'warning';
    }
    if (level) out.push({ locationId: r.locationId, lastSyncedAt: r.lastSyncedAt, level });
  }
  return out;
}

/* ---------- the slim strip ---------- */

/** The chip's noun after its bold count: "17 missed calls", "3 calls flagged for follow-up". */
export const CHIP_NOUN: Readonly<Record<HomePromptKey, (n: number) => string>> = {
  missed_calls: (n) => (n === 1 ? 'missed call' : 'missed calls'),
  voicemails: (n) => (n === 1 ? 'voicemail' : 'voicemails'),
  ai_calls: (n) => (n === 1 ? 'call flagged for follow-up' : 'calls flagged for follow-up'),
  after_hours_ai: (n) => (n === 1 ? 'after-hours AI call' : 'after-hours AI calls'),
  texts: (n) => (n === 1 ? 'unanswered text' : 'unanswered texts'),
  missed_call_replies: (n) =>
    n === 1 ? 'missed-call text reply unanswered' : 'missed-call text replies unanswered',
  leads: (n) => (n === 1 ? 'new lead' : 'new leads'),
  booking_requests: (n) => (n === 1 ? 'booking request' : 'booking requests'),
  portal_messages: (n) => (n === 1 ? 'portal message' : 'portal messages'),
  portal_requests: (n) => (n === 1 ? 'portal request' : 'portal requests'),
  answer_rate: () => 'unanswered, answer rate down',
};

/** The sync chip's noun: "2 offices not syncing". */
export const syncChipNoun = (n: number, delayed = false) =>
  delayed
    ? n === 1
      ? 'office sync delayed'
      : 'offices sync delayed'
    : n === 1
      ? 'office not syncing'
      : 'offices not syncing';

/** Rose when anything in the strip is urgent (a stopped sync always is). */
export function stripSeverity(
  shown: readonly HomePrompt[],
  syncProblemCount: number,
): 'urgent' | 'attention' {
  return syncProblemCount > 0 || shown.some((p) => p.severity === 'urgent')
    ? 'urgent'
    : 'attention';
}

/**
 * A chip's tooltip: the full sentence, then the oldest wait, flags, the
 * source detail, the office split and the window it counts
 * ("Oldest 2h 14m · McLaren 9 · Signature 8 · last 72 hours"). Pure: the age
 * format and office names come in.
 */
export function chipTooltip(
  p: HomePrompt,
  o: {
    split: string | null;
    age: (iso: string) => string;
    windowHours?: number | undefined;
    now: number;
  },
): string {
  const parts: string[] = [];
  if (p.oldestAt) parts.push(`oldest ${o.age(p.oldestAt)}`);
  if (p.urgentCount > 0 && p.key !== 'answer_rate') {
    parts.push(
      p.key === 'booking_requests' || p.key === 'portal_requests'
        ? `${p.urgentCount} past due`
        : `${p.urgentCount} marked urgent`,
    );
  }
  if (p.detail) parts.push(p.detail);
  if (o.split) parts.push(o.split);
  if (p.since && o.windowHours) {
    const windowStart = o.now - o.windowHours * 3_600_000;
    parts.push(
      Date.parse(p.since) > windowStart + 60_000
        ? `since Velora phones went live ${o.age(p.since)} ago`
        : `last ${o.windowHours} hours`,
    );
  }
  const head = PROMPT_META[p.key].title(p.count);
  return parts.length ? `${head}: ${parts.join(' · ')}` : head;
}
